import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Injector, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { NeoFormFieldModule } from '@neogov/pattern-library/neo-form-field';
import { NeoInputModule } from '@neogov/pattern-library/neo-input';
import {
  FieldErrorsComponent,
  OuterControlErrorStateMatcher,
  shouldSurfaceFieldError,
} from '../field-errors';

@Component({
  selector: 'cm-textarea-field',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NeoFormFieldModule, NeoInputModule, FieldErrorsComponent],
  templateUrl: './textarea-field.component.html',
  styleUrl: './textarea-field.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TextareaFieldComponent,
      multi: true,
    },
  ],
})
export class TextareaFieldComponent implements ControlValueAccessor {
  public readonly label = input<string>('');
  public readonly ariaLabel = input<string>('');
  public readonly placeholder = input<string>('');
  public readonly autId = input.required<string>();
  public readonly rows = input<number>(3);
  public readonly hint = input<string>('');
  public readonly maxlength = input<number | null>(null);
  public readonly minlength = input<number | null>(null);
  public readonly required = input<boolean>(false);
  public readonly formatted = input<boolean>(false);
  public readonly resizable = input<boolean>(true);
  public readonly subscriptSizing = input<'fixed' | 'dynamic'>('fixed');

  // Error handling inputs
  public readonly errorMessages = input<Partial<Record<string, string>>>({});
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');
  public readonly hideErrors = input<boolean>(false);

  public value: string | null = null;
  public disabled = false;

  private readonly _injector = inject(Injector);
  private readonly _cdr = inject(ChangeDetectorRef);
  public readonly ngControl = signal<NgControl | null>(null);

  /**
   * Feeds the OUTER control's validity into the native textarea so the pattern
   * library exposes aria-invalid and links the error via aria-describedby
   * (WCAG 3.3.1 / 4.1.2). See {@link OuterControlErrorStateMatcher}.
   */
  protected readonly errorStateMatcher = new OuterControlErrorStateMatcher(() =>
    shouldSurfaceFieldError(this.ngControl()?.control, this.showErrorsWhen(), this.hideErrors())
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (value: string | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  constructor() {
    // Defer NgControl lookup to avoid circular dependency
    // The NgControl directive will be available after the component is constructed
    queueMicrotask(() => {
      try {
        const ngControl = this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });
  }

  public writeValue(value: string | null): void {
    this.value = this.formatted() ? this.formatValue(value) : value;
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onTextareaChange(value: string): void {
    this.value = value;
    this.onChange(this.value);
  }

  public formatValue(value: string | null): string {
    if (!value) return '';
    // Strip markdown bold markers (**text** → text) for plain textarea display
    return value.replace(/\*\*(.+?)\*\*/g, '$1');
  }

  public onBlur(): void {
    // Simply call onTouched - no setTimeout needed!
    // The cm-field-errors component is subscribed to control.events
    // which emits TouchedChangeEvent synchronously when markAsTouched() is called
    this.onTouched();
  }
}
